import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Add, Archive, Delete, GetApp, Search } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import {
    baixarBase64,
    baixarSeloPremiado,
    baixarTodosSelosPremiados,
    criarSelosPremiados,
    excluirSeloPremiado,
} from "../../../services/seloPremiadoFornecedor"
import useLoadingStore from "../../../stores/LoadingStore"
import { SeloPremiadoFornecedorActionsTypes } from "../context/seloPremiadoFornecedorActions"
import { useSeloPremiadoFornecedorContext } from "../context/useSeloPremiadoFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#388e3c" },
    },
    codigoCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    descricaoCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}))

export function SelosPremiadosLista() {
    const classes = useStyles()
    const theme = useTheme()
    const { dados, dispatch, erroCarregamento, recarregar } = useSeloPremiadoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [dialogAberto, setDialogAberto] = useState(false)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleBuscar = () => {
        recarregar(dados.busca || undefined)
    }

    const handleBuscaKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleBuscar()
    }

    const handleAbrirDialog = () => {
        dispatch({ type: SeloPremiadoFornecedorActionsTypes.RESETAR_FORM })
        setDialogAberto(true)
    }

    const handleFecharDialog = () => {
        setDialogAberto(false)
    }

    const handleCampoNovo = (campo: keyof typeof dados.novoSelo, valor: string) => {
        dispatch({
            type: SeloPremiadoFornecedorActionsTypes.ATUALIZAR_CAMPO_NOVO,
            payload: { campo, valor },
        })
    }

    const handleSalvar = async () => {
        const { codigo, titulo, descricao, maximo, quantidadeAGerar, valorDesconto } =
            dados.novoSelo

        if (!codigo.trim()) {
            alertStart("Código é obrigatório.", "error")
            return
        }
        if (!titulo.trim()) {
            alertStart("Título é obrigatório.", "error")
            return
        }
        if (!descricao.trim()) {
            alertStart("Descrição é obrigatória.", "error")
            return
        }
        if (!maximo || parseInt(maximo) <= 0) {
            alertStart("Máximo de registros deve ser maior que zero.", "error")
            return
        }
        if (!quantidadeAGerar || parseInt(quantidadeAGerar) <= 0) {
            alertStart("Quantidade a gerar deve ser maior que zero.", "error")
            return
        }

        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await criarSelosPremiados(autorizacao, {
                codigo: codigo.trim(),
                titulo: titulo.trim(),
                descricao: descricao.trim(),
                maximo: parseInt(maximo),
                quantidadeAGerar: parseInt(quantidadeAGerar),
                valorDesconto: valorDesconto ? parseFloat(valorDesconto) : 0,
            })
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao criar selos.", "error")
                return
            }
            alertStart(
                response.criados === 1
                    ? "Selo criado com sucesso!"
                    : `${response.criados} selos criados com sucesso!`,
                "success"
            )
            setDialogAberto(false)
            await recarregar(dados.busca || undefined)
        } finally {
            setLoading(false)
        }
    }

    const handleExcluir = async (id: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await excluirSeloPremiado(autorizacao, id)
            if (response && "retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao excluir selo.", "error")
                return
            }
            alertStart("Selo excluído com sucesso!", "success")
            await recarregar(dados.busca || undefined)
        } finally {
            setLoading(false)
        }
    }

    const handleBaixarSelo = async (id: number) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await baixarSeloPremiado(autorizacao, id)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao gerar QR Code.", "error")
                return
            }
            baixarBase64(response.imagemBase64, `SeloPremiado${response.codigo}.png`, "image/png")
        } finally {
            setLoading(false)
        }
    }

    const handleBaixarTodos = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await baixarTodosSelosPremiados(autorizacao)
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao gerar ZIP.", "error")
                return
            }
            baixarBase64(response.zipBase64, response.nomeArquivo, "application/zip")
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Selos Premiados
                </Typography>
                <Typography
                    variant="body2"
                    style={{ color: "#7f8c8d", marginBottom: theme.spacing(3) }}
                >
                    Crie e gerencie QR Codes premiados para o seu estabelecimento.
                </Typography>

                {/* Filtro */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Pesquisar
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box display="flex" alignItems="center" style={{ gap: theme.spacing(2) }}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Buscar por código ou título..."
                                value={dados.busca}
                                onChange={e =>
                                    dispatch({
                                        type: SeloPremiadoFornecedorActionsTypes.SET_BUSCA,
                                        payload: e.target.value,
                                    })
                                }
                                onKeyDown={handleBuscaKeyDown}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search style={{ color: "#7f8c8d", fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="contained"
                                className={classes.saveButton}
                                onClick={handleBuscar}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                Pesquisar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Lista */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Selos Premiados
                        </Typography>
                        <Button
                            variant="contained"
                            className={classes.saveButton}
                            startIcon={<Add />}
                            onClick={handleAbrirDialog}
                        >
                            Novo Selo
                        </Button>
                    </Box>
                    <Box className={classes.cardContent}>
                        <TableContainer style={{ maxHeight: 450, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ width: "100%", minWidth: 810 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 90 }}>Código</TableCell>
                                        <TableCell style={{ minWidth: 110 }}>Data</TableCell>
                                        <TableCell style={{ minWidth: 90 }}>Usado</TableCell>
                                        <TableCell style={{ minWidth: 80 }}>Máx.</TableCell>
                                        <TableCell style={{ minWidth: 160 }}>Título</TableCell>
                                        <TableCell style={{ minWidth: 200 }}>Descrição</TableCell>
                                        <TableCell style={{ minWidth: 80 }} align="center">
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dados.selos.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                style={{ color: "#7f8c8d", padding: "24px" }}
                                            >
                                                Nenhum selo premiado encontrado.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        dados.selos.map(selo => (
                                            <TableRow key={selo.id} hover>
                                                <TableCell
                                                    className={classes.codigoCell}
                                                    title={selo.codigo}
                                                >
                                                    {selo.codigo}
                                                </TableCell>
                                                <TableCell>{selo.dataHoraInicio}</TableCell>
                                                <TableCell>{selo.quantidadeUtilizada}</TableCell>
                                                <TableCell>{selo.maximo}</TableCell>
                                                <TableCell
                                                    className={classes.codigoCell}
                                                    title={selo.brindeTitle ?? ""}
                                                >
                                                    {selo.brindeTitle ?? "-"}
                                                </TableCell>
                                                <TableCell
                                                    className={classes.descricaoCell}
                                                    title={selo.brindeDescricao ?? ""}
                                                >
                                                    {selo.brindeDescricao ?? "-"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    <Tooltip title="Baixar QR Code">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleBaixarSelo(selo.id)
                                                            }
                                                        >
                                                            <GetApp style={{ color: "#4caf50" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Excluir">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleExcluir(selo.id)}
                                                        >
                                                            <Delete style={{ color: "#e53935" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {dados.selos.length > 0 && (
                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Archive />}
                                    onClick={handleBaixarTodos}
                                    style={{ borderColor: "#4caf50", color: "#4caf50" }}
                                >
                                    Baixar todos os QR Codes
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Dialog - Novo Selo */}
            <Dialog open={dialogAberto} onClose={handleFecharDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Criar QR Code Premiado</DialogTitle>
                <DialogContent>
                    <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: theme.spacing(2), paddingTop: theme.spacing(1) }}
                    >
                        <TextField
                            label="Código"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            placeholder="ganhouAcai"
                            value={dados.novoSelo.codigo}
                            onChange={e => handleCampoNovo("codigo", e.target.value)}
                            helperText="Identificador único do QR Code"
                        />
                        <TextField
                            label="Título"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            placeholder="Que Sorte!!"
                            value={dados.novoSelo.titulo}
                            onChange={e => handleCampoNovo("titulo", e.target.value)}
                            helperText="Título exibido ao cliente ao ler o QR Code"
                        />
                        <TextField
                            label="Descrição"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            placeholder="Você ganhou R$5,00 de desconto"
                            value={dados.novoSelo.descricao}
                            onChange={e => handleCampoNovo("descricao", e.target.value)}
                            helperText="O que o cliente está ganhando ao registrar"
                        />
                        <TextField
                            label="Máximo de registros por usuário"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            type="number"
                            placeholder="1"
                            value={dados.novoSelo.maximo}
                            onChange={e => handleCampoNovo("maximo", e.target.value)}
                            helperText="Quantidade de vezes que o mesmo usuário pode usar este QR Code"
                        />
                        <TextField
                            label="QR Codes a criar"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            type="number"
                            placeholder="1"
                            value={dados.novoSelo.quantidadeAGerar}
                            onChange={e => handleCampoNovo("quantidadeAGerar", e.target.value)}
                            helperText="Quantos QR Codes diferentes serão gerados"
                        />
                        <TextField
                            label="Valor de desconto (apenas delivery)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            placeholder="5"
                            value={dados.novoSelo.valorDesconto}
                            onChange={e => handleCampoNovo("valorDesconto", e.target.value)}
                            helperText="Desconto aplicado automaticamente no próximo pedido (opcional)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions style={{ padding: theme.spacing(2) }}>
                    <Button onClick={handleFecharDialog}>Cancelar</Button>
                    <Button
                        variant="contained"
                        className={classes.saveButton}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
